package dev.lingvo.site.components.editor

import androidx.compose.runtime.Stable
import androidx.compose.runtime.mutableStateMapOf
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.MtTranslateResponse
import dev.lingvo.shared.SuggestionDto
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch

/** Owns Assist request identity, transport lifecycle, and its observable async state. */
@Stable
internal class EditorAssistAsyncStore(
    private val projectSlug: String,
    private val locale: String,
    private val scope: CoroutineScope,
    private val toasts: ToastState,
    private val canWriteTranslations: () -> Boolean,
    private val currentKeyId: () -> String?,
    private val shouldToastAssistOutcomes: () -> Boolean,
    private val assistStatuses: EditorAssistStatusStore,
    private val suggestionsRequest: suspend (keyId: String) -> List<SuggestionDto>,
    private val translateRequest: suspend (EditorGenerationClaim) -> MtTranslateResponse,
    private val matchedSourceRequest: suspend (sourceKeyName: String) -> String?,
) {
    val suggestions = mutableStateMapOf<String, Async<List<SuggestionDto>>>()
    val suggestionLoads = mutableStateMapOf<String, EditorSuggestionLoadRequest>()
    val machineSuggestions = mutableStateMapOf<String, List<EditorSuggestion.Machine>>()
    val matchedSources = mutableStateMapOf<String, String>()
    val generationStatuses = mutableStateMapOf<EditorGenerationKey, EditorGenerationStatus>()
    val translatingProviders: Map<String, Set<MtProvider>>
        get() = generationStatuses.values
            .filterIsInstance<EditorGenerationStatus.Running>()
            .groupBy(
                keySelector = { status -> status.claim.keyId },
                valueTransform = { status -> status.claim.provider },
            )
            .mapValues { (_, providers) -> providers.toSet() }

    private val suggestionStatusPublicationIds =
        mutableMapOf<EditorSuggestionLoadRequest, Long>()
    private var nextGenerationRequestId = 1L

    fun ensureLoaded(row: KeyRow): Job? {
        val request = beginSuggestionLoad(row) ?: return null
        return scope.launch { executeSuggestionLoad(request) }
    }

    fun retry(row: KeyRow, expectedFailure: Async.Failure): Job? {
        val keyId = row.key.id
        if (suggestions[keyId] !== expectedFailure || suggestionLoads[keyId] != null) return null
        suggestions.remove(keyId)
        val request = beginSuggestionLoad(row) ?: return null
        return scope.launch { executeSuggestionLoad(request) }
    }

    fun beginSuggestionLoad(row: KeyRow): EditorSuggestionLoadRequest? {
        val keyId = row.key.id
        if (suggestions[keyId] != null) return null
        val request = EditorSuggestionLoadRequest(keyId = keyId)
        suggestionLoads[keyId] = request
        suggestions[keyId] = Async.Loading
        suggestionStatusPublicationIds[request] = assistStatuses.publishTranslationMemory(
            projectSlug = projectSlug,
            locale = locale,
            keyId = keyId,
            eventKind = EditorAssistStatusEventKind.LOADING,
            message = tr(
                "editor.editor_assist_async_store.loading_translation_memory_suggestions",
                "Loading translation-memory suggestions.",
            ),
        ).publicationId
        return request
    }

    suspend fun executeSuggestionLoad(request: EditorSuggestionLoadRequest) {
        try {
            if (!request.isCurrent()) return
            val loaded = suggestionsRequest(request.keyId)
            if (!request.isCurrent()) return
            suggestions[request.keyId] = Async.Success(loaded)
            assistStatuses.publishTranslationMemory(
                projectSlug = projectSlug,
                locale = locale,
                keyId = request.keyId,
                eventKind = EditorAssistStatusEventKind.SUCCEEDED,
                message = if (loaded.isEmpty()) {
                    tr(
                        "editor.editor_assist_async_store.no_translation_memory_suggestions_found",
                        "No translation-memory suggestions found.",
                    )
                } else {
                    tr(
                        "editor.editor_assist_async_store.translation_memory_suggestions_are_ready",
                        "Translation-memory suggestions are ready.",
                    )
                },
            )
            loadMatchedSources(request.keyId, loaded)
        } catch (cancelled: CancellationException) {
            if (request.isCurrent() && suggestions[request.keyId] == Async.Loading) {
                val message = translationMemoryCancelledMessage()
                suggestions[request.keyId] = Async.Failure(message)
                assistStatuses.publishTranslationMemory(
                    projectSlug = projectSlug,
                    locale = locale,
                    keyId = request.keyId,
                    eventKind = EditorAssistStatusEventKind.CANCELLED,
                    message = message,
                )
            }
            throw cancelled
        } catch (failure: Throwable) {
            if (request.isCurrent()) {
                val failed = Async.Failure(
                    failure.message?.takeIf(String::isNotBlank)
                        ?: tr(
                            "editor.editor_assist_async_store.couldnt_load_suggestions_retry",
                            "Couldn't load suggestions. Retry?",
                        ),
                )
                suggestions[request.keyId] = failed
                assistStatuses.publishTranslationMemory(
                    projectSlug = projectSlug,
                    locale = locale,
                    keyId = request.keyId,
                    eventKind = EditorAssistStatusEventKind.FAILED,
                    message = translationMemoryFailureMessage(failed.message),
                )
            }
        } finally {
            suggestionStatusPublicationIds.remove(request)
            if (suggestionLoads[request.keyId] === request) {
                suggestionLoads.remove(request.keyId)
            }
        }
    }

    fun activeSuggestionStatusPublicationId(keyId: String): Long? =
        suggestionLoads[keyId]?.let(suggestionStatusPublicationIds::get)

    /**
     * A translation-memory match is one flat string, so it is never offered on a plural key.
     *
     * [EditorSuggestion.TranslationMemory] carries no plural forms — the server's memory query
     * selects a single `t.value` — and nothing decides which CLDR category a flat string belongs
     * to. Rendered on a plural key the card draws "No text returned" under every category, and
     * inserting it writes an empty string into *every* form of the draft. Nothing stops that:
     * placeholder validation compares the flat value against the OTHER source form and passes, so
     * the result is badged clean, the button reads a plain "Insert", `Alt+n` applies it without a
     * review step, and the emptied draft autosaves 800ms later over the translator's work.
     *
     * The candidate cannot be represented on this key, so it is not offered on this key.
     */
    fun rankedSuggestions(row: KeyRow): List<EditorSuggestion> {
        val memory = if (row.key.isPlural) {
            emptyList()
        } else {
            (suggestions[row.key.id] as? Async.Success)
                ?.value
                .orEmpty()
                .mapIndexed { index, item ->
                    EditorSuggestion.TranslationMemory(
                        id = "tm-${row.key.id}-$index-${item.sourceKeyName}",
                        value = item.value,
                        similarity = item.similarity,
                        sourceKeyName = item.sourceKeyName,
                        matchedSource = matchedSources[sourceLookupKey(row.key.id, item.sourceKeyName)],
                        warning = suggestionWarning(row, item.value),
                    )
                }
        }
        return machineSuggestions[row.key.id].orEmpty() + memory
    }

    fun translate(row: KeyRow, provider: MtProvider): Job? {
        val request = beginGeneration(row, provider) ?: return null
        return scope.launch { executeGeneration(request) }
    }

    fun beginGeneration(row: KeyRow, provider: MtProvider): EditorGenerationRequest? {
        val claim = EditorGenerationClaim(projectSlug, locale, row.key.id, provider)
        if (!canWriteTranslations() || currentKeyId() != claim.keyId) return null
        if (generationStatuses[claim.key] is EditorGenerationStatus.Running) return null
        val status = publishGenerationStatus(
            claim = claim,
            eventKind = EditorAssistStatusEventKind.RUNNING,
            message = trf(
                "editor.editor_assist_async_store.generating_a_suggestion_with_provider",
                "Generating a suggestion with {provider}.",
            "provider" to claim.provider.displayName),
        )
        return EditorGenerationRequest(
            claim = claim,
            row = row,
            requestId = nextGenerationRequestId++,
            statusPublicationId = status.publicationId,
        ).also { request ->
            generationStatuses[claim.key] = EditorGenerationStatus.Running(
                claim = claim,
                requestId = request.requestId,
                sequence = status.publicationId,
            )
        }
    }

    suspend fun executeGeneration(request: EditorGenerationRequest) {
        var terminalPublished = false
        try {
            if (!request.isLive()) return
            val response = try {
                translateRequest(request.claim)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                terminalPublished = publishGenerationFailure(request, failure)
                return
            }
            if (!request.isLive()) return
            val result = response.results.firstOrNull { result ->
                result.keyId == request.claim.keyId
            }
            if (result == null) {
                machineSuggestions.removeProviderSuggestion(
                    keyId = request.claim.keyId,
                    provider = request.claim.provider,
                )
                val message = trf(
                    "editor.editor_assist_async_store.provider_didnt_return_a_suggestion_retry",
                    "{provider} didn't return a suggestion. Retry?",
                    "provider" to request.claim.provider.displayName,
                )
                val status = publishGenerationStatus(
                    claim = request.claim,
                    eventKind = EditorAssistStatusEventKind.NO_RESULT,
                    message = message,
                    toastFeedback = EditorAssistToastFeedback(message, LToastVariant.WARNING),
                )
                generationStatuses[request.claim.key] = EditorGenerationStatus.NoResult(
                    claim = request.claim,
                    message = message,
                    sequence = status.publicationId,
                )
                terminalPublished = true
                showToastOutsideAssist(status)
                return
            }
            val shapeIssue = result.insertabilityIssue(request.row, locale)
            if (shapeIssue != null) {
                if (!request.isLive()) return
                val prefix = trf(
                    "editor.editor_assist_async_store.provider_didnt_return_an_insertable_suggestion",
                    "{provider} didn't return an insertable suggestion.",
                    "provider" to request.claim.provider.displayName,
                )
                val message = result.rejectionReason
                    ?.takeIf(String::isNotBlank)
                    ?.let { reason -> "$prefix $reason" }
                    ?: "$prefix $shapeIssue"
                machineSuggestions.removeProviderSuggestion(
                    keyId = request.claim.keyId,
                    provider = request.claim.provider,
                )
                val status = publishGenerationStatus(
                    claim = request.claim,
                    eventKind = EditorAssistStatusEventKind.NO_RESULT,
                    message = message,
                    toastFeedback = EditorAssistToastFeedback(message, LToastVariant.WARNING),
                )
                generationStatuses[request.claim.key] = EditorGenerationStatus.NoResult(
                    claim = request.claim,
                    message = message,
                    sequence = status.publicationId,
                )
                terminalPublished = true
                showToastOutsideAssist(status)
                return
            }
            val validationWarning = if (request.row.key.isPlural) {
                pluralSuggestionWarning(request.row, locale, result.pluralForms)
            } else {
                suggestionWarning(request.row, result.value)
            }
            val warning = result.rejectionReason
                ?.takeIf(String::isNotBlank)
                ?: validationWarning
                ?: if (!result.accepted) {
                    tr(
                        "editor.editor_assist_async_store.provider_marked_this_suggestion_for_review",
                        "Provider marked this suggestion for review.",
                    )
                } else {
                    null
                }
            val suggestion = EditorSuggestion.Machine(
                id = "ai-${request.claim.keyId}-${request.claim.provider.id}",
                provider = request.claim.provider,
                value = result.value,
                pluralForms = result.pluralForms,
                accepted = result.accepted,
                warning = warning,
            )
            if (!request.isLive()) return
            machineSuggestions[request.claim.keyId] = machineSuggestions[request.claim.keyId]
                .orEmpty()
                .filterNot { machine -> machine.provider == request.claim.provider } + suggestion
            val status = publishGenerationStatus(
                claim = request.claim,
                eventKind = EditorAssistStatusEventKind.SUCCEEDED,
                message = trf(
                    "editor.editor_assist_async_store.suggestion_from_provider_is_ready",
                    "Suggestion from {provider} is ready.",
                "provider" to request.claim.provider.displayName),
                toastFeedback = EditorAssistToastFeedback(
                    message = trf(
                        "editor.editor_assist_async_store.suggestion_from_provider_is_ready_in_assist",
                        "Suggestion from {provider} is ready in Assist.",
                    "provider" to request.claim.provider.displayName),
                    variant = LToastVariant.SUCCESS,
                ),
            )
            generationStatuses[request.claim.key] = EditorGenerationStatus.Succeeded(
                claim = request.claim,
                suggestion = suggestion,
                sequence = status.publicationId,
            )
            terminalPublished = true
            showToastOutsideAssist(status)
        } catch (cancelled: CancellationException) {
            if (request.hasPublishedRunningStatus()) {
                publishGenerationStatus(
                    claim = request.claim,
                    eventKind = EditorAssistStatusEventKind.CANCELLED,
                    message = trf(
                        "editor.editor_assist_async_store.generation_with_provider_was_cancelled",
                        "Generation with {provider} was cancelled.",
                    "provider" to request.claim.provider.displayName),
                )
                terminalPublished = true
            }
            throw cancelled
        } finally {
            val running = generationStatuses[request.claim.key] as? EditorGenerationStatus.Running
            if (running?.requestId == request.requestId) {
                if (!terminalPublished) {
                    publishGenerationStatus(
                        claim = request.claim,
                        eventKind = EditorAssistStatusEventKind.CLEARED,
                        message = trf(
                            "editor.editor_assist_async_store.generation_with_provider_stopped",
                            "Generation with {provider} stopped.",
                        "provider" to request.claim.provider.displayName),
                    )
                }
                generationStatuses.remove(request.claim.key)
            }
        }
    }

    suspend fun loadMatchedSources(keyId: String, items: List<SuggestionDto>) = coroutineScope {
        val loaded = items.map { item -> item.sourceKeyName }.distinct().map { sourceKeyName ->
            async {
                val lookupKey = sourceLookupKey(keyId, sourceKeyName)
                if (matchedSources[lookupKey] != null) return@async null
                val source = try {
                    matchedSourceRequest(sourceKeyName)
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (_: Throwable) {
                    null
                }
                source?.let { value -> lookupKey to value }
            }
        }.awaitAll()
        loaded.filterNotNull().forEach { (lookupKey, source) ->
            matchedSources[lookupKey] = source
        }
    }

    private fun publishGenerationFailure(
        request: EditorGenerationRequest,
        failure: Throwable,
    ): Boolean {
        if (!request.isLive()) return false
        val message = failure.message
            ?.takeIf(String::isNotBlank)
            ?.let { detail ->
                val prefix = trf(
                    "editor.editor_assist_async_store.provider_couldnt_generate_a_suggestion",
                    "{provider} couldn't generate a suggestion.",
                    "provider" to request.claim.provider.displayName,
                )
                "$prefix $detail"
            }
            // One key rather than that sentence plus a bare "Retry?": a sentence assembled from two
            // catalogue entries is two fragments to a translator and cannot be reordered by one.
            ?: trf(
                "editor.editor_assist_async_store.provider_couldnt_generate_a_suggestion_retry",
                "{provider} couldn't generate a suggestion. Retry?",
                "provider" to request.claim.provider.displayName,
            )
        val status = publishGenerationStatus(
            claim = request.claim,
            eventKind = EditorAssistStatusEventKind.FAILED,
            message = message,
            toastFeedback = EditorAssistToastFeedback(message, LToastVariant.DANGER),
        )
        generationStatuses[request.claim.key] = EditorGenerationStatus.Failed(
            claim = request.claim,
            message = message,
            sequence = status.publicationId,
        )
        showToastOutsideAssist(status)
        return true
    }

    private fun publishGenerationStatus(
        claim: EditorGenerationClaim,
        eventKind: EditorAssistStatusEventKind,
        message: String,
        toastFeedback: EditorAssistToastFeedback? = null,
    ): EditorAssistStatusPublication = assistStatuses.publishProvider(
        claim = claim,
        eventKind = eventKind,
        message = message,
        toastFeedback = toastFeedback,
    )

    private fun showToastOutsideAssist(publication: EditorAssistStatusPublication) {
        if (!shouldToastAssistOutcomes()) return
        publication.toastFeedback?.let { feedback ->
            toasts.show(feedback.message, feedback.variant)
        }
    }

    private fun EditorSuggestionLoadRequest.isCurrent(): Boolean =
        suggestionLoads[keyId] === this && suggestions[keyId] == Async.Loading

    private fun EditorGenerationRequest.hasPublishedRunningStatus(): Boolean {
        val running = generationStatuses[claim.key] as? EditorGenerationStatus.Running
        return running?.requestId == requestId && running.sequence == statusPublicationId
    }

    /**
     * This request still owns its key's generation slot — wherever the translator has since gone.
     *
     * Separate from [isCurrent] because the two questions were conflated, and the answer to the
     * wrong one was throwing away work somebody paid for. A generation writes only into maps keyed
     * by *its own* key — `machineSuggestions[claim.keyId]`, `generationStatuses[claim.key]` — so
     * nothing it does can disturb whatever row is on screen now. Requiring the translator to still
     * be looking at the key meant that advancing during a slow provider call discarded the finished
     * suggestion and spent the credits anyway; the `finally` below then replaced it with
     * "Generation stopped", so returning to the key showed no trace of what was bought.
     */
    private fun EditorGenerationRequest.isLive(): Boolean {
        if (!canWriteTranslations()) return false
        val running = generationStatuses[claim.key] as? EditorGenerationStatus.Running
        return running?.requestId == requestId && running.claim == claim
    }

    /** [isLive], and the translator is still on the key — for anything that touches the open row. */
    private fun EditorGenerationRequest.isCurrent(): Boolean =
        isLive() && currentKeyId() == claim.keyId

    private fun sourceLookupKey(keyId: String, sourceKeyName: String): String =
        "$keyId::$sourceKeyName"
}
